import type { ReactNode } from 'react';

import { isNonFormPdfRecordType, type PdfExportRecordType } from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { TooltipArrow, TooltipPortal } from '@radix-ui/react-tooltip';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { usePermissions } from '@/hooks/use-permissions';
import { openExportTab } from '@/utils/export-tab';

import DownloadPdfIcon from './icons/download-pdf';
import RefreshIcon from './icons/refresh';

type SingleRecordPdfControlsProps = {
  recordType: PdfExportRecordType;
  recordId: string;
  /** The persisted document URL, when the record already has a generated PDF. */
  documentUrl: string | null | undefined;
};

function IconButtonWithTooltip({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
  return (
    <TooltipProvider>
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>
          <Button variant="ghost" size="sm" type="button" aria-label={label} onClick={onClick} className="flex items-center gap-2">
            {children}
          </Button>
        </TooltipTrigger>
        <TooltipPortal>
          <TooltipContent className="bg-default-text pointer-events-none border-none text-xs text-white">
            <TooltipArrow className="fill-default-text" />
            {label}
          </TooltipContent>
        </TooltipPortal>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Download / regenerate controls for a single-record PDF. The Download button
 * serves the persisted document when present and, when it's missing,
 * (re)generates through the common export service in a dedicated progress tab so
 * the user can keep working here. Superusers get an extra control that forces a
 * regeneration.
 */
export function SingleRecordPdfControls({ recordType, recordId, documentUrl }: SingleRecordPdfControlsProps) {
  const { isSuperUser } = usePermissions();
  const isNonForm = isNonFormPdfRecordType(recordType);

  const regenerate = () => {
    openExportTab({ kind: 'pdf', request: { recordType, recordId } });
  };

  const handleDownload = () => {
    if (documentUrl) {
      window.open(documentUrl, '_blank');
      return;
    }
    regenerate();
  };

  return (
    <>
      <IconButtonWithTooltip label="Download PDF" onClick={handleDownload}>
        <DownloadPdfIcon className="h-4 w-4" />
      </IconButtonWithTooltip>
      {/* Non-form records never persist a document, so Download already
          regenerates — a separate Regenerate control would be a duplicate. */}
      {isSuperUser && !isNonForm && (
        <IconButtonWithTooltip label="Regenerate PDF" onClick={regenerate}>
          <RefreshIcon size={20} />
        </IconButtonWithTooltip>
      )}
    </>
  );
}
